import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getPathLanguage } from "@/lib/i18n";
import { translateText } from "@/lib/translations";

const translatableAttributes = ["placeholder", "aria-label", "title"];
const AUTO_TRANSLATE_OPT_OUT_SELECTOR = '[data-auto-translate="false"]';

const isAutoTranslateDisabled = (node) => {
  const element = node?.nodeType === Node.ELEMENT_NODE ? node : node?.parentElement;
  return Boolean(element?.closest?.(AUTO_TRANSLATE_OPT_OUT_SELECTOR));
};

const walkAndTranslate = (root, lang) => {
  if (!root || lang === "en" || isAutoTranslateDisabled(root)) {
    return;
  }

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node;
  while ((node = walker.nextNode())) {
    if (isAutoTranslateDisabled(node)) {
      continue;
    }

    const raw = node.nodeValue;
    const trimmed = raw?.trim();
    if (!trimmed) {
      continue;
    }
    const translated = translateText(trimmed, lang);
    if (translated !== trimmed) {
      node.nodeValue = raw.replace(trimmed, translated);
    }
  }

  const elements = root.querySelectorAll("*");
  elements.forEach((el) => {
    if (isAutoTranslateDisabled(el)) {
      return;
    }

    translatableAttributes.forEach((attr) => {
      const value = el.getAttribute(attr);
      if (!value) {
        return;
      }
      const translated = translateText(value, lang);
      if (translated !== value) {
        el.setAttribute(attr, translated);
      }
    });
  });
};

export default function AutoTranslate() {
  const location = useLocation();

  useEffect(() => {
    const lang = getPathLanguage(location.pathname);
    if (lang === "en") {
      return;
    }

    walkAndTranslate(document.body, lang);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            walkAndTranslate(node, lang);
          }
          if (node.nodeType === Node.TEXT_NODE && node.nodeValue?.trim()) {
            if (isAutoTranslateDisabled(node)) {
              return;
            }
            const trimmed = node.nodeValue.trim();
            const translated = translateText(trimmed, lang);
            if (translated !== trimmed) {
              node.nodeValue = node.nodeValue.replace(trimmed, translated);
            }
          }
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [location.pathname]);

  return null;
}
